import React, { useState } from "react";
import { ChevronRight, ChevronDown, GripVertical, Trash2 } from "lucide-react";

const TreeNode = ({
  node,
  level = 0,
  onDragStart,
  onDrop,
  onToggle,
  expandedNodes,
  onDeleteTopic,
  onDeleteCategory,
  onDeleteSubcategory,
  onDeleteIndicator,
  onAddCategory,
  onAddSubcategory,
  onAddIndicator,
  onEditNode,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes[node.id] !== false;
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    onDragStart(node);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    onDrop(node);
  };

  const getNodeTypeStyle = () => {
    if (node.id.includes("_i"))
      return { backgroundColor: "#f3e8ff", borderColor: "#d8b4fe" };
    if (node.id.includes("_s"))
      return { backgroundColor: "#dbeafe", borderColor: "#93c5fd" };
    if (node.id.includes("_c"))
      return { backgroundColor: "#dcfce7", borderColor: "#86efac" };
    if (node.id.includes("_t"))
      return { backgroundColor: "#fef9c3", borderColor: "#fde047" };
    return { backgroundColor: "#f3f4f6", borderColor: "#d1d5db" };
  };

  const getNodeTypeLabel = () => {
    if (node.id.includes("_i")) return "Indicator";
    if (node.id.includes("_s")) return "Subcategory";
    if (node.id.includes("_c")) return "Category";
    if (node.id.includes("_t")) return "Topic";
    return "Portal";
  };

  const baseStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px",
    marginBottom: "4px",
    borderRadius: "4px",
    border: "1px solid",
    cursor: level > 0 ? "move" : "default",
    transition: "all 0.2s ease",
    ...getNodeTypeStyle(),
    ...(isDraggingOver
      ? {
          boxShadow: "0 0 0 2px #3b82f6, 0 6px 10px rgba(0,0,0,0.08)",
          transform: "scale(1.01)",
        }
      : {}),
  };

const isIndicator = node.id.includes("_i");
const isSubcategory = !isIndicator && node.id.includes("_s");
const isCategory = !isSubcategory && !isIndicator && node.id.includes("_c");
const isTopic = !isCategory && !isSubcategory && !isIndicator && node.id.includes("_t");

/*
  console.log(
  "NODE TYPE CHECK:",
  node.id,
  { isTopic, isCategory, isSubcategory }
);
*/

  return (
    <div style={{ marginLeft: `${level * 24}px` }}>
      <div
        draggable={level > 0}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={baseStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Expand / Collapse */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggle(node.id);
          }}
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
          ) : (
            <div style={{ width: "16px" }} />
          )}
        </div>

        {/* Drag Handle */}
        {level > 0 && <GripVertical size={16} style={{ color: "#9ca3af" }} />}

        {/* Node Info */}
<div style={{ flex: 1 }}>
<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
  <div style={{ fontWeight: 500 }}>{node.label}</div>

  {level > 0 && isHovered && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        console.log("EDIT CLICK TREE NODE:", node);
        onEditNode?.(node);

      }}
      style={{
        background: "transparent",
        border: "1px solid #cbd5e1",
        borderRadius: 4,
        padding: "2px 6px",
        cursor: "pointer",
        fontSize: "12px",
      }}
      title="Edit"
    >
      Edit
    </button>
  )}
</div>

  <div style={{ fontSize: "12px", color: "#6b7280" }}>
    {getNodeTypeLabel()} • ID: {node.id}
  </div>
</div>


        {/* Hover Actions */}
        {isHovered && (
          <div style={{ display: "flex", gap: 6 }}>
            {/* Add Category on Topics */}
            {isTopic && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddCategory?.(node);
                }}
                title="Add category under this topic"
                style={{
                  background: "#e0f2fe",
                  color: "#0369a1",
                  border: "1px solid #bae6fd",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                + Add Category
              </button>
            )}

            {/* Add Subcategory on Categories */}
            {isCategory && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddSubcategory?.(node);
                }}
                title="Add subcategory under this category"
                style={{
                  background: "#dcfce7",
                  color: "#166534",
                  border: "1px solid #bbf7d0",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                + Add Subcategory
              </button>
            )}

            {/* Add Indicator on any node except indicator itself */}
            {!node.id.includes("_i") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddIndicator?.(node);
                }}
                title="Add indicator under this element"
                style={{
                  background: "#ede9fe",
                  color: "#5b21b6",
                  border: "1px solid #ddd6fe",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                + Add Indicator
              </button>
            )}

            {/* Delete buttons for topic/category/subcategory */}
            {/* Delete button — one per node type */}
              {(isTopic || isCategory || isSubcategory || isIndicator) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();

                     console.log("DELETE CLICK", {
    id: node.id,
    isTopic,
    isCategory,
    isSubcategory,
    isIndicator,
  });

                    if (isTopic) onDeleteTopic?.(node);
                    else if (isCategory) onDeleteCategory?.(node);
                    else if (isSubcategory) onDeleteSubcategory?.(node);
                    else if (isIndicator) onDeleteIndicator?.(node);
                  }}
                  title={`Delete ${isTopic ? "topic" : isCategory ? "category" : isSubcategory ? "subcategory" : "indicator"}`}
                  style={deleteBtnStyle}
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
          </div>
        )}
      </div>

      {/* Render children recursively */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onDragStart={onDragStart}
              onDrop={onDrop}
              onToggle={onToggle}
              expandedNodes={expandedNodes}
              onDeleteTopic={onDeleteTopic}
              onDeleteCategory={onDeleteCategory}
              onDeleteSubcategory={onDeleteSubcategory}
              onDeleteIndicator={onDeleteIndicator}
              onAddCategory={onAddCategory}
              onAddSubcategory={onAddSubcategory}
              onAddIndicator={onAddIndicator}
              onEditNode={onEditNode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const deleteBtnStyle = {
  background: "#fee2e2",
  color: "#b91c1c",
  border: "1px solid #fecaca",
  borderRadius: "4px",
  padding: "4px 8px",
  cursor: "pointer",
  fontSize: "12px",
  display: "flex",
  alignItems: "center",
  gap: 4,
};

export default TreeNode;




